import { CircleCheck, AlertCircle, Clock } from 'lucide-react';

/**
 * How a comparison's delta reads, once `positive_direction` has been applied.
 *
 * The figure itself still carries its own sign — `+11.9%` is `+11.9%` whether
 * that is good news or bad. The colour is the verdict, and it is the only
 * part that flips: a spend KPI up 11.9% prints a red `+11.9%`, which is the
 * two facts a reader needs and not one dressed as the other.
 */
export const TREND_TEXT = Object.freeze({
  up: 'text-emerald-600 dark:text-emerald-400',
  down: 'text-red-600 dark:text-red-400',
  neutral: 'text-content',
});

export const TREND_CONFIG = Object.freeze({
  up: {
    icon: CircleCheck,
    bgColor: 'Success',
    iconColor: 'Green',
  },
  down: {
    icon: AlertCircle,
    bgColor: 'Error',
    iconColor: 'Error',
  },
  neutral: {
    icon: Clock,
    bgColor: 'Alert',
    iconColor: 'Alert',
  },
});

/**
 * The bar's colours, keyed by **tone** — the vocabulary `color_token` resolves
 * to — with the old trend keys kept beside them.
 *
 * A KPI's bar used to take its colour from the trend, which says whether the
 * number moved up or down. v2 sends `color_token`, which says whether it is
 * *good*: the backend evaluated the author's thresholds against whichever
 * number `thresholds_applies_to` named. A rise that is still below target is
 * a rise and a warning at the same time, and only the second is worth a
 * colour.
 */
export const TREND_PROGRESS = Object.freeze({
  // Tones, from `color_token`. `neutral` is listed on purpose: it means the
  // backend evaluated the thresholds and none matched, which is an answer —
  // leaving it out would let the bar fall through to the trend and paint a
  // colour the author's rules did not ask for.
  neutral:  { track: 'bg-blue-100 dark:bg-blue-950/40',       fill: 'bg-blue-500 dark:bg-blue-400' },
  success:  { track: 'bg-emerald-100 dark:bg-emerald-950/40', fill: 'bg-emerald-500 dark:bg-emerald-400' },
  warning:  { track: 'bg-amber-100 dark:bg-amber-950/40',     fill: 'bg-amber-500 dark:bg-amber-400' },
  critical: { track: 'bg-red-100 dark:bg-red-950/40',         fill: 'bg-red-500 dark:bg-red-400' },
  info:     { track: 'bg-blue-100 dark:bg-blue-950/40',       fill: 'bg-blue-500 dark:bg-blue-400' },
  // Trend, for a payload with no token.
  up:      { track: 'bg-blue-100 dark:bg-blue-950/40',   fill: 'bg-blue-500 dark:bg-blue-400' },
  down:    { track: 'bg-red-100 dark:bg-red-950/40',     fill: 'bg-red-500 dark:bg-red-400' },
  default: { track: 'bg-blue-100 dark:bg-blue-950/40',   fill: 'bg-blue-500 dark:bg-blue-400' },
});

export { KPI_STATE_COPY } from '../../../../constants';
